import tinyColor from 'tinycolor2';

import { BasicColorSet, ColorSet, OPACITY } from '@optimus-web/local-tokens';

import { alpha } from './duplicatedUtils';
import * as palette from './palette';
import { ColorScale } from './types';

export const basic: BasicColorSet = {
    base: palette.neutral.base,

    background: palette.neutral.base,
    backgroundDark: palette.neutral[800],
    backgroundLight: palette.neutral[25],
    backgroundSecondary: palette.neutral[25],
    backgroundTertiary: palette.neutral[50],

    foreground: palette.white,

    text: palette.neutral[1000],
    textSecondary: alpha(palette.neutral[1000], OPACITY.secondary),

    textContrast: palette.white,
    textSecondaryContrast: palette.white,

    hover: palette.neutral[700],
    hoverLight: palette.neutral[25],

    focus: palette.neutral[1000],
    focusLight: palette.neutral[25],

    active: palette.neutral[800],
    activeLight: palette.neutral[100],

    border: palette.neutral[100],
    placeholder: palette.neutral[200],
    label: palette.neutral[300],

    imageBackground: 'transparent',
    imagePlaceholder: palette.neutral[50],

    dataVisualizationBackground: palette.neutral[300],
};

export const basicDark: BasicColorSet = {
    base: palette.neutralDark[800],

    background: palette.neutralDark[800],
    backgroundDark: palette.neutralDark[900],
    backgroundLight: palette.neutralDark[700],
    backgroundSecondary: palette.neutralDark[900],
    backgroundTertiary: palette.neutralDark[400],

    foreground: palette.neutralDark.base,

    text: palette.white,
    textSecondary: alpha(palette.white, OPACITY.secondary),

    textContrast: palette.white,
    textSecondaryContrast: palette.neutralDark[1000],

    hover: palette.neutralDark[1000],
    hoverLight: palette.neutralDark[300],

    focus: palette.neutralDark[1000],
    focusLight: palette.neutralDark[300],

    active: palette.neutralDark[1000],
    activeLight: palette.neutralDark[300],

    border: palette.neutralDark[200],
    placeholder: alpha(palette.white, OPACITY.secondary),
    label: alpha(palette.white, OPACITY.secondary),

    imageBackground: 'transparent',
    imagePlaceholder: palette.neutralDark[700],

    dataVisualizationBackground: palette.neutralDark[300],
};

const getBackgroundSecondary = (color: ColorScale, isDarkTheme?: boolean) => (isDarkTheme ? color[900] : color[100]);

export const generateColorSet = (color: ColorScale, isDarkTheme?: boolean): ColorSet => ({
    base: color.base,

    background: color.base,
    backgroundLight: getBackgroundSecondary(color, isDarkTheme),
    backgroundSecondary: getBackgroundSecondary(color, isDarkTheme),
    backgroundTertiary: color.base,

    hover: color[800],
    hoverLight: color[100],

    focus: color.base,
    focusLight: color[100],

    active: color[900],
    activeLight: color[200],

    text: color.base,

    textContrast: tinyColor(color.base).isDark() ? palette.white : palette.neutral[1000],
    textSecondaryContrast: tinyColor(getBackgroundSecondary(color, isDarkTheme)).isDark()
        ? palette.white
        : palette.neutral[1000],

    border: color.base,

    dataVisualizationBackground: color[300],
});

export const primary: ColorSet = generateColorSet(palette.blue);
export const info: ColorSet = generateColorSet(palette.infoBlue);
export const error: ColorSet = generateColorSet(palette.red);
export const success: ColorSet = generateColorSet(palette.green);
export const warning: ColorSet = generateColorSet(palette.orange);

export const primaryDark: ColorSet = generateColorSet(palette.blueDark);
export const infoDark: ColorSet = generateColorSet(palette.infoBlueDark);
export const errorDark: ColorSet = generateColorSet(palette.redDark);
export const successDark: ColorSet = generateColorSet(palette.greenDark);
export const warningDark: ColorSet = generateColorSet(palette.orangeDark);

export const disabled: ColorSet = {
    base: palette.neutral.base,

    background: palette.neutral[200],
    backgroundLight: palette.neutral[50],
    backgroundSecondary: palette.neutral[50],
    backgroundTertiary: palette.neutral[50],

    hover: palette.neutral[50],
    hoverLight: palette.neutral[100],

    focus: palette.neutral[50],
    focusLight: palette.neutral[100],

    active: palette.neutral[1000],
    activeLight: palette.neutral[200],

    text: palette.neutral[200],
    textContrast: palette.neutral[400],
    textSecondaryContrast: palette.neutral[1000],

    border: palette.neutral[50],

    dataVisualizationBackground: palette.neutral[300],
};

export const disabledDark: ColorSet = {
    ...disabled,
    text: palette.neutral[200],
};
